import { useState, useCallback, useEffect, useRef } from 'react';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Check, ChevronRight, TriangleAlert } from 'lucide-react';

import { CREATE_GPT } from '../../graphql/mutations';
import {
  muscularGroupsTemplate,
  equipmentsTemplate,
  muscularGroupsTemplateEn,
  equipmentsTemplateEn,
  muscularGroupIcons,
  equipmentIcons,
  workoutSplits,
} from '../../utils/templates';
import { trackEvent } from '../../utils/gt';
import { PreviewArt } from '../app/PreviewArt';
import { useWakeLock } from '../../utils/useWakeLock';
import { aiLimitOf, aiLimitText } from '../../utils/aiLimits';

import '../../styles/pages/treinos.css';

const roundToHalf = (value) => Math.round(value * 2) / 2;

// Merge consecutive entries that share the same name, reps, and weight into a single entry with combined series count
const mergeConsecutiveSets = (exercises) => {
  const result = [];
  for (const ex of exercises) {
    const prev = result[result.length - 1];
    if (prev && prev.name === ex.name && prev.reps === ex.reps && prev.weight === ex.weight && prev.weightUnit === ex.weightUnit) {
      result[result.length - 1] = { ...prev, series: String(parseInt(prev.series) + parseInt(ex.series)) };
    } else {
      result.push(ex);
    }
  }
  return result;
};

const applyWarmupToFirstExercise = (exercises, warmupLabel) => {
  if (!exercises.length) return exercises;
  const withoutWarmup = exercises.filter((ex) => !/(aquecimento|warmup)/i.test(ex.name));
  const first = withoutWarmup[0];
  const baseWeight = parseFloat(first.weight) || 0;
  const warmupWeight = baseWeight > 0 ? String(roundToHalf(baseWeight * 0.4)) : first.weight;
  const makeWarmup = () => ({ ...first, name: `${first.name} (${warmupLabel})`, series: '1', reps: '10', interval: 40, weight: warmupWeight, warmup: true });
  return mergeConsecutiveSets([makeWarmup(), makeWarmup(), ...withoutWarmup]);
};

const applyLowVolumeStructure = (exercises, warmupLabel, feederLabel) => {
  const filtered = exercises.filter((ex) => !/(aquecimento|warmup)/i.test(ex.name));
  const expanded = filtered.flatMap((exercise, index) => {
    const baseWeight = parseFloat(exercise.weight) || 0;
    const warmupWeight = baseWeight > 0 ? String(roundToHalf(baseWeight * 0.4)) : exercise.weight;
    const feeder75Weight = baseWeight > 0 ? String(roundToHalf(baseWeight * 0.75)) : exercise.weight;
    const feeder90Weight = baseWeight > 0 ? String(roundToHalf(baseWeight * 0.9)) : exercise.weight;
    const entries = [];
    if (index === 0) {
      entries.push({ ...exercise, name: `${exercise.name} (${warmupLabel})`, series: '1', reps: '12', interval: 40, weight: warmupWeight, warmup: true });
    }
    entries.push(
      { ...exercise, name: `${exercise.name} (${feederLabel})`, series: '1', reps: '6', interval: 40, weight: feeder75Weight, warmup: true },
      { ...exercise, name: `${exercise.name} (${feederLabel})`, series: '1', reps: '2', interval: 40, weight: feeder90Weight, warmup: true },
      { ...exercise, series: '1', reps: '9' },
      { ...exercise, series: '1', reps: '9' }
    );
    return entries;
  });
  return mergeConsecutiveSets(expanded);
};

const VOLUMES = ['low', 'moderate', 'high'];
const VOLUME_EXERCISE_COUNT = { low: '6 to 8', moderate: '6 to 8', high: '6 to 10' };
const MAX_WORKOUTS = 10;
const MAX_RETRIES = 2;

// Steps of each path; generating and error are shared screens
const AI_STEPS = ['muscles', 'gear', 'volume'];
const SPLIT_STEPS = ['split', 'splitVolume', 'splitGear'];

// The templates are in Title Case and go into the prompt as they are; on screen pt reads in sentence case
// ("Somente o corpo", "Corpo inteiro 5x na semana"). Names with a slash are lists of proper names ("Push / Pull / Legs")
const sentenceCase = (label) =>
  label.includes('/') ? label : label.replace(/(\s)(\p{Lu})(\p{Ll}+)/gu, (_, space, first, rest) => space + first.toLowerCase() + rest);

/**
 * How to build a new workout: with the AI, a whole split, or from scratch. Opens the new-workout pane (and the empty
 * Meus Treinos on desktop). `letter` is the one the new workout takes.
 */
export const WorkoutStartChooser = ({ isPro, count, limit, letter, onPick, onCancel }) => {
  const { t } = useTranslation();
  const b = (key, options) => t(`pages.Workout.builder.${key}`, options);
  const isFirst = count === 0;
  const options = [
    { key: 'ai', art: 'aiWorkout', isPro: true },
    { key: 'split', art: 'letters', isPro: true },
    { key: 'zero', art: 'scratch', badge: 'plus' },
  ];

  return (
    <div className="mt-tr-new">
      <h2 className="mt-tr-new__title" tabIndex={-1}>
        {isFirst ? b('firstTitle') : b('newTitle', { letter })}
      </h2>
      <p className="mt-tr-new__sub">{isFirst ? b('firstSub') : b('newSub', { count, limit })}</p>
      <ul className="mt-tr-opts">
        {options.map(({ key, art, badge, isPro: needsPro }) => (
          <li key={key}>
            <button type="button" className="mt-tr-opt" onClick={() => onPick(key)}>
              <PreviewArt kind={art} badge={badge} />
              <span className="mt-tr-opt__text">
                <span className="mt-tr-opt__title">
                  {b(`opt.${key}.title`)}
                  {needsPro && !isPro && <span className="mt-tr-pro">{b('pro')}</span>}
                </span>
                <span className="mt-tr-opt__desc">{b(`opt.${key}.desc`)}</span>
              </span>
              <ChevronRight className="mt-tr-opt__chevron" aria-hidden="true" />
            </button>
          </li>
        ))}
      </ul>
      {onCancel && (
        <div className="mt-tr-new__foot">
          <button type="button" className="mt-tr-btn mt-tr-btn--quiet" onClick={onCancel}>
            {b('cancel')}
          </button>
        </div>
      )}
    </div>
  );
};

// Muscle groups and equipment as tiles with their pictograms (owner: keep the icons)
const PictogramGrid = ({ label, options, labels, icons, selected, onToggle }) => (
  <ul className="mt-tr-picks" aria-label={label}>
    {options.map((option, index) => {
      const isSelected = selected.includes(option);
      return (
        <li key={option}>
          <button type="button" className="mt-tr-pick" aria-pressed={isSelected} onClick={() => onToggle(option)}>
            {isSelected && (
              <span className="mt-tr-pick__check" aria-hidden="true">
                <Check />
              </span>
            )}
            {icons?.[index] && <img className="mt-tr-pick__icon" src={icons[index]} alt="" />}
            <span className="mt-tr-pick__label">{labels[index]}</span>
          </button>
        </li>
      );
    })}
  </ul>
);

const VolumeList = ({ selected, label, warning, onPick }) => (
  <>
    {warning && (
      <p className="mt-tr-note">
        <TriangleAlert aria-hidden="true" />
        {warning}
      </p>
    )}
    <ul className="mt-tr-opts">
      {VOLUMES.map((volume) => (
        <li key={volume}>
          <button type="button" className="mt-tr-opt mt-tr-opt--compact" aria-pressed={selected === volume} onClick={() => onPick(volume)}>
            <span className="mt-tr-opt__text">
              <span className="mt-tr-opt__title">{label(`${volume}Volume`)}</span>
              <span className="mt-tr-opt__desc">{label(`${volume}VolumeDesc`)}</span>
            </span>
          </button>
        </li>
      ))}
    </ul>
  </>
);

/**
 * The new-workout pane: the start choice, then the AI's steps (muscles, equipment, volume) or a split's, in place.
 * A custom workout goes to the editor through onGenerated; a split goes to the split review through onSplitReady.
 */
export const WorkoutBuilder = ({
  isPro,
  letter,
  count,
  limit,
  workoutData,
  initialStep = 'choose',
  onManual,
  onGenerated,
  onSplitReady,
  onProRequired,
  onSplitLimitExceeded,
  onCancel,
}) => {
  const { t, i18n: { language } } = useTranslation();
  const ai = (key, options) => t(`components.ModalCreationAI.${key}`, options);
  const b = (key, options) => t(`pages.Workout.builder.${key}`, options);
  const isPt = language === 'pt';
  const shown = (label) => (isPt ? sentenceCase(label) : label);

  const muscleOptions = isPt ? muscularGroupsTemplate : muscularGroupsTemplateEn;
  const equipmentOptions = isPt ? equipmentsTemplate : equipmentsTemplateEn;
  const muscleDefault = ai('muscularGroupDefault');
  const equipmentExclusive = [ai('equipmentDefault'), ai('equipmentWholeGym')];

  // Equipment goes to the agent in English whatever the language: the exercise database is in English
  const toEnglishEquipments = useCallback(
    (selected) => {
      if (!isPt) return selected;
      return selected.map((eq) => {
        const idx = equipmentsTemplate.indexOf(eq);
        return idx !== -1 ? equipmentsTemplateEn[idx] : eq;
      });
    },
    [isPt]
  );

  const [step, setStep] = useState(initialStep);
  const [selectedSplit, setSelectedSplit] = useState(null);
  const [selectedVolume, setSelectedVolume] = useState(null);
  const [trainingVariables, setTrainingVariables] = useState(() => ({ muscularGroups: [muscleDefault], equipments: [ai('equipmentDefault')] }));
  const [currentGenerationIndex, setCurrentGenerationIndex] = useState(0);
  // The API's daily or per-minute AI limit that turned the last request down (utils/aiLimits), shown on the error screen
  const [aiLimit, setAiLimit] = useState(null);

  // Refs for the generation queue — avoids stale closure issues across async calls
  const generationIndexRef = useRef(0);
  const generatedWorkoutsRef = useRef([]);
  const lastGptPromptRef = useRef(null);
  const retryCountRef = useRef(0);
  const processedGptDataRef = useRef(null);
  const rootRef = useRef(null);
  const isFirstStep = useRef(true);

  const [createGptMutation, { data: createGptData }] = useMutation(CREATE_GPT);

  const isGenerating = step === 'generating' || step === 'splitGenerating';
  // Keep the screen awake while the AI works
  useWakeLock(isGenerating);

  // Each new step takes the focus on its heading, so a screen reader hears where it is
  useEffect(() => {
    if (isFirstStep.current) {
      isFirstStep.current = false;
      return;
    }
    rootRef.current?.querySelector('h2')?.focus({ preventScroll: true });
    // The new step starts at its top (on phones the last one could be scrolled far down)
    if (rootRef.current?.getBoundingClientRect().top < 0) window.scrollTo({ top: 0 });
  }, [step]);

  const sendPrompt = useCallback(
    (prompt) => {
      lastGptPromptRef.current = prompt;
      processedGptDataRef.current = null;
      setAiLimit(null);
      // A rejected request (network, server, refused) must never leave the spinner running
      createGptMutation({ variables: { gptInput: { gptMessageToSend: prompt } } }).catch((error) => {
        retryCountRef.current = 0;
        setAiLimit(aiLimitOf(error));
        setStep('error');
      });
    },
    [createGptMutation]
  );

  const splitPrompt = (split, sessionIndex) => {
    const sessions = isPt ? split.workoutsPt : split.workoutsEn;
    const splitName = isPt ? split.namePt : split.nameEn;
    const session = sessions[sessionIndex];
    const volumeExercisesRange = selectedVolume === 'high' ? '6 to 10' : '6 to 8';
    return [
      `Language: ${isPt ? 'Portuguese' : 'English'}`,
      `Split: ${splitName}`,
      `Session: ${session.label}`,
      `Muscles: ${session.muscles}`,
      `Equipment: ${toEnglishEquipments(trainingVariables.equipments).join(', ')}`,
      `Volume: ${selectedVolume ?? 'moderate'} (low volume means fewer sets per exercise, NOT fewer exercises — always cover all muscle groups for the session)`,
      `Number of exercises: ${volumeExercisesRange}`,
      `Include a "weight" field (numeric, in ${isPt ? 'kg' : 'lbs'}) for each exercise (heavier for low volume/strength, lighter for high volume/hypertrophy)`,
      `IMPORTANT: "reps" must be a single fixed number (e.g. 10), never a range (e.g. 8-12). Do not modify the number of sets/series — keep them appropriate for the selected volume.`,
      `Prefer common, well-known exercises (e.g. bench press, squat, deadlift, rows, overhead press, curls) over obscure or overly complex variations.`,
    ].join('\n');
  };

  useEffect(() => {
    if (!createGptData || createGptData === processedGptDataRef.current) return;
    processedGptDataRef.current = createGptData;

    // Names come from the library as they are (no Title Case on every word)
    const sanitize = (exercises) =>
      exercises.map((exercise) => ({
        ...exercise,
        // The API names each exercise as the library does ("Supino Reto com Barra"): kept as is, never recased
        name: (exercise.name || exercise.namePt || '').trim(),
        series: String(exercise.series ?? '3').match(/\d+/)?.[0] ?? '3',
        reps: String(exercise.reps ?? '12').match(/\d+/)?.[0] ?? '12',
        weight: String(exercise.weight ?? exercise.startingWeightKg ?? '').match(/[\d.]+/)?.[0] ?? '',
        weightUnit: isPt ? 'kg' : 'lbs',
      }));

    try {
      const workoutsGptObj = JSON.parse(createGptData.createGpt.data.gptResponse);
      workoutsGptObj.exercises = sanitize(workoutsGptObj.exercises);
      // Warm-ups are applied exactly once: the low-volume structure already carries its own
      workoutsGptObj.exercises =
        selectedVolume === 'low'
          ? applyLowVolumeStructure(workoutsGptObj.exercises, ai('warmupLabel'), ai('feederLabel'))
          : applyWarmupToFirstExercise(workoutsGptObj.exercises, ai('warmupLabel'));

      if (selectedSplit) {
        // ── Split path: add the session to the queue, fire the next one or finish ──
        const sessions = isPt ? selectedSplit.workoutsPt : selectedSplit.workoutsEn;
        const currentIdx = generationIndexRef.current;
        generatedWorkoutsRef.current = [
          ...generatedWorkoutsRef.current,
          { ...workoutsGptObj, label: sessions[currentIdx].label, description: sessions[currentIdx].label },
        ];

        const nextIdx = currentIdx + 1;
        generationIndexRef.current = nextIdx;
        setCurrentGenerationIndex(nextIdx);

        if (nextIdx < sessions.length) {
          sendPrompt(splitPrompt(selectedSplit, nextIdx));
        } else {
          onSplitReady(generatedWorkoutsRef.current);
        }
      } else {
        // ── Custom path: hand the workout to the editor ──
        onGenerated({ ...workoutData, ...workoutsGptObj });
      }
      retryCountRef.current = 0;
    } catch {
      // The reply wasn't the JSON we asked for: resend the same prompt (max 2 retries), then say so
      if (retryCountRef.current < MAX_RETRIES) {
        retryCountRef.current += 1;
        sendPrompt(lastGptPromptRef.current);
      } else {
        retryCountRef.current = 0;
        setStep('error');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createGptData]);

  const toggleMuscle = (muscle) => {
    trackEvent(`muscular_group_${muscle}`, `Muscular Group: ${muscle}`, 'modal_creation_ai', true);
    setTrainingVariables((prev) => {
      const { muscularGroups } = prev;
      if (muscle === muscleDefault) return { ...prev, muscularGroups: [muscleDefault] };
      if (muscularGroups.includes(muscle)) return { ...prev, muscularGroups: muscularGroups.filter((m) => m !== muscle) };
      return { ...prev, muscularGroups: [...muscularGroups.filter((m) => m !== muscleDefault), muscle] };
    });
  };

  const toggleEquipment = (equipment) => {
    trackEvent(`equipment_${equipment}`, `Equipment: ${equipment}`, 'modal_creation_ai', true);
    setTrainingVariables((prev) => {
      const { equipments } = prev;
      if (equipmentExclusive.includes(equipment)) return { ...prev, equipments: [equipment] };
      if (equipments.includes(equipment)) return { ...prev, equipments: equipments.filter((e) => e !== equipment) };
      return { ...prev, equipments: [...equipments.filter((e) => !equipmentExclusive.includes(e)), equipment] };
    });
  };

  const pick = (how) => {
    if (how === 'zero') {
      trackEvent('modal_creation_ai_manual', 'Modal Creation AI - Manual', 'modal_creation_ai', true);
      onManual();
      return;
    }
    if (how === 'ai') {
      trackEvent('modal_creation_ai_artificial_intelligence', 'Modal Creation AI - Artificial Intelligence', 'modal_creation_ai', true);
      // Free accounts go through the steps too: the Pro offer comes at "Gerar", their choices made (the hook)
      setSelectedSplit(null);
      setStep('muscles');
      return;
    }
    trackEvent('modal_creation_predefined_splits', 'Modal Creation - Pre-defined Splits', 'modal_creation_ai', true);
    setStep('split');
  };

  const generateCustom = () => {
    if (!isPro) {
      onProRequired('custom');
      return;
    }
    const volume = selectedVolume;
    trackEvent(`workout_volume_${volume}`, `Workout Volume: ${volume}`, 'modal_creation_ai', true);
    retryCountRef.current = 0;
    sendPrompt(
      [
        `Language: ${isPt ? 'Portuguese' : 'English'}`,
        `Muscular groups: ${trainingVariables.muscularGroups.join(', ')}`,
        `Equipment: ${toEnglishEquipments(trainingVariables.equipments).join(', ')}`,
        `Volume: ${volume} (low volume = fewer sets per exercise, heavier weight; moderate = 3–4 sets; high = 4–5 sets)`,
        `Number of exercises: ${VOLUME_EXERCISE_COUNT[volume]}`,
        `Include a "weight" field (numeric, in ${isPt ? 'kg' : 'lbs'}) for each exercise (heavier for low volume/strength, lighter for high volume/hypertrophy)`,
        `IMPORTANT: "reps" must be a single fixed number (e.g. 10), never a range (e.g. 8-12).`,
        `Prefer common, well-known exercises (e.g. bench press, squat, deadlift, rows, overhead press, curls) over obscure or overly complex variations.`,
      ].join('\n')
    );
    setStep('generating');
  };

  const chooseSplit = (split) => {
    const sessionCount = (isPt ? split.workoutsPt : split.workoutsEn).length;
    const slotsAvailable = MAX_WORKOUTS - (count ?? 0);
    if (sessionCount > slotsAvailable) {
      onSplitLimitExceeded(sessionCount - slotsAvailable, sessionCount);
      return;
    }
    trackEvent(`workout_split_${split.nameEn.toLowerCase().replace(/[\s/]/g, '_')}`, `Workout Split: ${split.nameEn}`, 'modal_creation_predefined', true);
    setSelectedSplit(split);
    setStep('splitVolume');
  };

  const generateSplit = () => {
    if (!isPro) {
      onProRequired('split');
      return;
    }
    generationIndexRef.current = 0;
    generatedWorkoutsRef.current = [];
    retryCountRef.current = 0;
    setCurrentGenerationIndex(0);
    sendPrompt(splitPrompt(selectedSplit, 0));
    setStep('splitGenerating');
  };

  const retry = () => {
    retryCountRef.current = 0;
    sendPrompt(lastGptPromptRef.current);
    setStep(selectedSplit ? 'splitGenerating' : 'generating');
  };

  if (step === 'choose') {
    return (
      <div ref={rootRef}>
        <WorkoutStartChooser isPro={isPro} count={count} limit={limit} letter={letter} onPick={pick} onCancel={onCancel} />
      </div>
    );
  }

  const isSplitPath = SPLIT_STEPS.includes(step) || step === 'splitGenerating' || (step === 'error' && !!selectedSplit);
  const pathSteps = isSplitPath ? SPLIT_STEPS : AI_STEPS;
  const stepIndex = pathSteps.indexOf(step);

  const backTargets = {
    muscles: () => setStep('choose'),
    gear: () => setStep('muscles'),
    volume: () => setStep('gear'),
    split: () => setStep('choose'),
    splitVolume: () => {
      setSelectedSplit(null);
      setStep('split');
    },
    splitGear: () => setStep('splitVolume'),
    error: () => setStep(selectedSplit ? 'splitGear' : 'volume'),
  };
  const goBack = backTargets[step];

  const splitName = selectedSplit && shown(isPt ? selectedSplit.namePt : selectedSplit.nameEn);
  const title = isSplitPath ? (splitName ? b('splitWithName', { name: splitName }) : b('splitTitle')) : b('aiTitle', { letter });

  let stepTitle = null;
  let stepSub = null;
  let body = null;
  let next = null;

  if (step === 'muscles') {
    stepTitle = b('musclesTitle');
    body = (
      <PictogramGrid
        label={b('musclesTitle')}
        options={muscleOptions}
        labels={muscleOptions.map(shown)}
        icons={muscularGroupIcons}
        selected={trainingVariables.muscularGroups}
        onToggle={toggleMuscle}
      />
    );
    next = { label: b('next'), onClick: () => setStep('gear'), disabled: !trainingVariables.muscularGroups.length };
  } else if (step === 'gear' || step === 'splitGear') {
    stepTitle = b('gearTitle');
    body = (
      <PictogramGrid
        label={b('gearTitle')}
        options={equipmentOptions}
        labels={equipmentOptions.map(shown)}
        icons={equipmentIcons}
        selected={trainingVariables.equipments}
        onToggle={toggleEquipment}
      />
    );
    next =
      step === 'gear'
        ? { label: b('next'), onClick: () => setStep('volume'), disabled: !trainingVariables.equipments.length }
        : { label: b('generate'), onClick: generateSplit, disabled: !trainingVariables.equipments.length, isGenerate: true };
  } else if (step === 'volume' || step === 'splitVolume') {
    stepTitle = b('volumeTitle');
    const hasLimitedEquipment =
      step === 'volume' && trainingVariables.equipments.length <= 2 && !trainingVariables.equipments.includes(ai('equipmentWholeGym'));
    body = <VolumeList selected={selectedVolume} label={ai} warning={hasLimitedEquipment ? ai('limitedEquipmentWarning') : null} onPick={setSelectedVolume} />;
    next =
      step === 'volume'
        ? { label: b('generate'), onClick: generateCustom, disabled: !selectedVolume, isGenerate: true }
        : {
            label: b('next'),
            disabled: !selectedVolume,
            onClick: () => {
              trackEvent(`workout_volume_${selectedVolume}`, `Workout Volume: ${selectedVolume}`, 'modal_creation_ai', true);
              setStep('splitGear');
            },
          };
  } else if (step === 'split') {
    stepSub = b('splitSub');
    body = (
      <ul className="mt-tr-opts">
        {workoutSplits.map((split) => {
          const sessions = isPt ? split.workoutsPt : split.workoutsEn;
          return (
            <li key={split.nameEn}>
              <button type="button" className="mt-tr-opt mt-tr-opt--compact" onClick={() => chooseSplit(split)}>
                <span className="mt-tr-opt__text">
                  <span className="mt-tr-opt__title">{shown(isPt ? split.namePt : split.nameEn)}</span>
                  <span className="mt-tr-opt__desc">{ai('splitWorkoutCount', { count: sessions.length })}</span>
                </span>
                <ChevronRight className="mt-tr-opt__chevron" aria-hidden="true" />
              </button>
            </li>
          );
        })}
      </ul>
    );
  }

  if (isGenerating) {
    const sessions = selectedSplit ? (isPt ? selectedSplit.workoutsPt : selectedSplit.workoutsEn) : [];
    const total = sessions.length;
    const { muscularGroups } = trainingVariables;
    const isWholeBody = muscularGroups.length === 1 && muscularGroups[0] === muscleDefault;
    return (
      <div className="mt-tr-new mt-tr-gen" ref={rootRef} role="status">
        <h2 className="mt-tr-new__title" tabIndex={-1}>
          {step === 'splitGenerating' ? b('generatingSplitTitle') : b('generatingTitle', { letter })}
        </h2>
        {step === 'splitGenerating' ? (
          <>
            <p className="mt-tr-new__sub">
              {ai('generatingSplit', { current: Math.min(currentGenerationIndex + 1, total), total, name: sessions[currentGenerationIndex]?.label ?? '' })}
            </p>
            <span className="mt-tr-gen__bar" aria-hidden="true">
              <span style={{ width: `${total ? (currentGenerationIndex / total) * 100 : 0}%` }} />
            </span>
          </>
        ) : (
          <p className="mt-tr-new__sub">
            {isWholeBody ? b('generatingSubAll') : b('generatingSub', { muscles: muscularGroups.map(shown).join(', ').toLowerCase() })}
          </p>
        )}
        <div className="mt-tr-gen__rows" aria-hidden="true">
          {[0, 1, 2, 3, 4].map((index) => (
            <span key={index} className="mt-tr-skel" />
          ))}
        </div>
      </div>
    );
  }

  if (step === 'error') {
    // A limit says which one and when it lifts; a daily one has nothing to retry until then
    const limitText = aiLimit && aiLimitText(t, aiLimit, { locale: isPt ? 'pt-BR' : 'en-US' });
    return (
      <div className="mt-tr-new" ref={rootRef} role="alert">
        <span className="mt-tr-gen__error" aria-hidden="true">
          <TriangleAlert />
        </span>
        <h2 className="mt-tr-new__title" tabIndex={-1}>
          {limitText ? limitText.title : t('components.ModalCreationAI.builder.errorTitle')}
        </h2>
        <p className="mt-tr-new__sub">{limitText ? limitText.body : t('components.ModalCreationAI.builder.errorBody')}</p>
        <div className="mt-tr-new__foot">
          <button type="button" className="mt-tr-btn mt-tr-btn--quiet" onClick={goBack}>
            {b('back')}
          </button>
          {(!limitText || limitText.canRetry) && (
            <button type="button" className="mt-tr-btn mt-tr-btn--go" onClick={retry}>
              {t('components.ModalCreationAI.builder.retry')}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-tr-new mt-tr-steps" ref={rootRef}>
      <h2 className="mt-tr-new__title" tabIndex={-1}>
        {title}
      </h2>
      {stepIndex >= 0 && (
        <div className="mt-tr-stepper">
          <span className="mt-tr-stepper__bars" aria-hidden="true">
            {pathSteps.map((pathStep, index) => (
              <i key={pathStep} className={index <= stepIndex ? 'is-on' : ''} />
            ))}
          </span>
          <span>{b('stepOf', { n: stepIndex + 1, total: pathSteps.length })}</span>
        </div>
      )}
      {stepTitle && <h3 className="mt-tr-steps__title">{stepTitle}</h3>}
      {stepSub && <p className="mt-tr-new__sub mt-tr-steps__sub">{stepSub}</p>}
      {body}
      <div className="mt-tr-new__foot">
        <button type="button" className="mt-tr-btn mt-tr-btn--quiet" onClick={goBack}>
          {b('back')}
        </button>
        {next && (
          <button type="button" className="mt-tr-btn mt-tr-btn--go" onClick={next.onClick} disabled={next.disabled}>
            {next.label}
          </button>
        )}
      </div>
    </div>
  );
};
